import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/motion";
import { TextField, SelectField, TextAreaField } from "@/components/Field";
import { submitCarrier } from "@/lib/api";
import { EQUIPMENT_OPTIONS, AREA_OPTIONS, CONTACT } from "@/lib/data";

const EXPECT = [
  "Clear load details before you commit",
  "Professional, respectful communication",
  "A single dispatch contact per load",
  "Prompt handling of delivery paperwork",
  "Payment per agreed written terms",
];

const EMPTY = {
  name: "", company: "", email: "", phone: "",
  equipment_type: "", fleet_size: "", operating_area: "",
  mc_dot: "", availability: "", notes: "",
};

export default function Carriers() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitCarrier(form);
      toast.success("Application received. Our carrier team will be in touch.");
      setForm(EMPTY);
    } catch {
      toast.error("Submission failed. Please try again or email dispatch directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-16" data-testid="carriers-page">
      <section className="border-b border-line">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <SectionLabel>// For Carriers &amp; Owner-Operators</SectionLabel>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-bright sm:text-5xl lg:text-6xl">
            Haul with a broker <span className="text-hazard">who respects the truck.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#9CA3AF] sm:text-lg">
            We're building a network of professional, properly qualified and appropriately insured carriers — owner-operators and small to mid-size fleets with suitable equipment and operating authority.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-5 lg:px-8">
        <Reveal className="lg:col-span-2">
          <h2 className="font-display text-2xl font-extrabold uppercase text-bright sm:text-3xl">What carriers can expect</h2>
          <div className="mt-8 flex flex-col gap-4">
            {EXPECT.map((item, i) => (
              <Reveal key={item} delay={i * 0.06} y={12}>
                <div className="flex items-start gap-3 border border-line bg-panel p-4">
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-hazard" />
                  <p className="text-sm leading-relaxed text-[#C9CDD4]">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 border border-line bg-panel p-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brass">Dispatch direct</p>
            <a href={`mailto:${CONTACT.dispatch}`} data-testid="carriers-email-link" className="mt-2 block font-mono text-sm text-hazard hover:underline">
              {CONTACT.dispatch}
            </a>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-3" delay={0.1}>
          <form onSubmit={onSubmit} className="border border-line bg-panel" data-testid="carrier-form">
            <div className="hazard-stripes h-1.5 w-full" />
            <div className="p-7 sm:p-9">
              <div className="flex items-baseline justify-between">
                <h2 className="font-display text-2xl font-extrabold uppercase text-bright">Join Our Carrier Network</h2>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim">Form CRN-02</span>
              </div>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <TextField label="Contact / Owner Name" name="name" value={form.name} onChange={onChange} required testId="carrier-name-input" />
                <TextField label="Company Name" name="company" value={form.company} onChange={onChange} testId="carrier-company-input" />
                <TextField label="Email" name="email" type="email" value={form.email} onChange={onChange} required testId="carrier-email-input" />
                <TextField label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} required testId="carrier-phone-input" />
                <SelectField label="Equipment Type" name="equipment_type" value={form.equipment_type} onChange={onChange} options={EQUIPMENT_OPTIONS} required testId="carrier-equipment-select" />
                <TextField label="Available Units" name="fleet_size" value={form.fleet_size} onChange={onChange} placeholder="e.g. 2 trucks / 3 trailers" testId="carrier-fleet-input" />
                <SelectField label="Operating Area / Preferred Lanes" name="operating_area" value={form.operating_area} onChange={onChange} options={AREA_OPTIONS} required testId="carrier-area-select" />
                <TextField label="MC / DOT / NSC Number" name="mc_dot" value={form.mc_dot} onChange={onChange} testId="carrier-mcdot-input" />
              </div>
              <div className="mt-5 grid gap-5">
                <TextField label="Availability" name="availability" value={form.availability} onChange={onChange} placeholder="e.g. Weekdays, immediate, seasonal…" testId="carrier-availability-input" />
                <TextAreaField label="Notes" name="notes" value={form.notes} onChange={onChange} placeholder="Lanes you run regularly, certifications, trailer specs…" testId="carrier-notes-textarea" />
              </div>
              <button
                type="submit"
                data-testid="carrier-submit-btn"
                disabled={sending}
                className="mt-8 w-full bg-hazard px-6 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-asphalt transition-colors duration-300 hover:bg-hazard-dark disabled:opacity-50"
              >
                {sending ? "Transmitting…" : "Submit Carrier Application"}
              </button>
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                Insurance &amp; authority verification completed during onboarding
              </p>
            </div>
          </form>
        </Reveal>
      </section>
    </main>
  );
}
